import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { QuoteToolEnvironment } from '@lx/quote-tool/lite/shared/types';
import { DANGEROUS_URL_PROTOCOLS } from '@lx/quote-tool/lite/shared/utils';
import { LoggerService } from '@lx/shared/logger';
import { ENVIRONMENT, getCompany } from '@lx/shared/tokens';
import { COMPANIES } from '@lx/shared/types';
import { valueIsNullOrUndefined } from '@lx/shared/utils';

/**
 * Service to manage return URL functionality for Quote Tool applications.
 * Handles redirection back to the parent portal when users exit the application.
 *
 * This service is shared between quote-tool-lite and online-quote-tool.
 *
 * The return URL is set via configuration when the service is initialized during APP_INITIALIZER.
 * If no return URL is provided, navigation features fall back to default behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class ReturnUrlService {
  private returnUrl: string | null = null;
  private backButtonLabel: string | null = null;

  private readonly company = getCompany();
  private readonly document = inject(DOCUMENT);
  private readonly logger = inject(LoggerService);
  private readonly environment = inject<QuoteToolEnvironment>(ENVIRONMENT);

  /**
   * Initializes the service by reading the returnUrl from configuration.
   * Should be called during application initialization (APP_INITIALIZER).
   */
  initialize(returnUrl: string | undefined): void {
    if (this.isSafeReturnUrl(returnUrl)) {
      this.returnUrl = returnUrl;
      this.backButtonLabel = $localize`Back to ${this.getCompanyName()}`;
    }
  }

  /**
   * Gets the return URL if it exists.
   * @returns The return URL string or null if not set
   */
  getReturnUrl(): string | null {
    return this.returnUrl;
  }

  /**
   * Checks if a return URL is available.
   * @returns True if return URL exists, false otherwise
   */
  hasReturnUrl(): boolean {
    return !valueIsNullOrUndefined(this.returnUrl) && this.returnUrl.trim() !== '';
  }

  /**
   * Validates that the provided return URL is safe to navigate to.
   * A URL is considered safe if it is either:
   * - A relative path starting with '/', or
   * - An absolute URL matching one of the allowed domain patterns (protects against malicious URLs)
   *
   * Security considerations:
   * - Blocks dangerous protocols (javascript:, data:, file:, etc.)
   * - Validates against known trusted domain patterns
   * - Allows cross-origin navigation to legitimate portals
   *
   * @param returnUrl The return URL to validate.
   * @returns True if the URL is safe, false otherwise.
   */
  private isSafeReturnUrl(returnUrl: string | undefined): returnUrl is string {
    if (valueIsNullOrUndefined(returnUrl)) {
      return false;
    }

    const trimmedUrl = returnUrl.trim();
    if (trimmedUrl === '') {
      return false;
    }

    // Block dangerous protocols
    const lowerCaseUrl = trimmedUrl.toLowerCase();
    const dangerousProtocol = DANGEROUS_URL_PROTOCOLS.some((protocol) => lowerCaseUrl.startsWith(protocol));

    if (dangerousProtocol) {
      this.logger.warn('Blocked return URL with dangerous protocol:', trimmedUrl);
      return false;
    }

    // Allow same-origin relative paths (safe by default). Block protocol-relative URLs ("//example.com").
    if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) {
      return true;
    }

    // For absolute URLs, validate against allowed domain patterns
    const window = this.document.defaultView;
    if (!window || !window.location) {
      return false;
    }

    try {
      // Parse the URL to validate its structure
      const targetUrl = new URL(trimmedUrl, window.location.href);

      // Check if the URL matches any of the allowed domain patterns from environment
      const allowedPatterns = this.environment.ALLOWED_RETURN_URL_DOMAIN_PATTERNS;
      const allowed = allowedPatterns.some((pattern) => pattern.test(targetUrl.href));

      if (!allowed) {
        this.logger.warn('Return URL does not match allowed domain patterns:', trimmedUrl);
      }

      return allowed;
    } catch {
      // If the URL cannot be parsed, treat it as unsafe
      this.logger.warn('Failed to parse return URL:', trimmedUrl);
      return false;
    }
  }

  /**
   * Navigates to the return URL by setting window.location.href.
   * If no valid return URL is set, this method does nothing.
   *
   * Uses document.defaultView.location for better testability and SSR compatibility.
   */
  navigateToReturnUrl(): void {
    const window = this.document.defaultView;
    if (!window || !this.returnUrl) {
      return;
    }
    window.location.href = this.returnUrl;
  }

  /**
   * Gets the formatted company name for display in the UI.
   * @returns Formatted company name (e.g., "Kaplan", "Alpadia") or the raw company value if not recognized
   */
  getCompanyName(): string {
    switch (this.company) {
      case COMPANIES.KAPLAN:
        return 'Kaplan';
      case COMPANIES.POSITIVELY_LANGUAGES:
        return 'Positively Languages';
      case COMPANIES.ALPADIA:
        return 'Alpadia';
      default:
        return this.company;
    }
  }

  /**
   * Gets the back button label with the company name.
   * @returns Label string for the back button (e.g., "← Back to Kaplan portal") or null if not initialized
   */
  getBackButtonLabel(): string | null {
    return this.backButtonLabel;
  }
}
